import { libraryRoot } from "./library-ownership";
import { useState, type DragEvent } from "react";
import {
  ChevronRight,
  Folder,
  FolderOpen,
  HardDrive,
  Building2,
} from "lucide-react";
import { Button } from "../../design-system";
import type {
  LibraryAdapter,
  LibraryFolderRow,
  LibraryStatus,
} from "./library-adapter";
import { useLibraryAdapter, descendantIds } from "./manuscript-library-data";
import {
  useLibraryResource,
  libraryError,
  invalidateLibrary,
} from "./library-runtime";
export const libraryDragType = "application/x-meta-crew-library";
export type LibraryTransfer = {
  customerId: string;
  entries: { id: string; kind: "folder" | "manuscript" }[];
};
export function folderCounts(folders: LibraryFolderRow[], id: string) {
  const ids = new Set(descendantIds(folders, id));
  const counts: Partial<Record<LibraryStatus, number>> = {};
  for (const f of folders.filter((f) => ids.has(f.id)))
    for (const [s, n] of Object.entries(f.counts))
      counts[s as LibraryStatus] = (counts[s as LibraryStatus] ?? 0) + (n ?? 0);
  return counts;
}
export function LibraryFolderTree({
  customerId,
  value,
  onSelect,
  onMove,
  provider,
  onDropItems,
  canDrop,
  rootLabel,
}: {
  customerId: string;
  value: string;
  onSelect: (id: string) => void;
  onMove?: (id: string, parent: string | null) => void | Promise<void>;
  provider?: LibraryAdapter;
  onDropItems?: (e: DragEvent, folder: string | null) => void;
  canDrop?: (folder: string | null) => boolean;
  rootLabel?: string;
}) {
  const adapter = useLibraryAdapter(provider),
    resource = useLibraryResource(adapter, "folders:" + customerId, () =>
      adapter.folders(customerId),
    );
  const folders = resource.data?.folders ?? [];
  const [collapsed, setCollapsed] = useState<string[]>([]),
    [error, setError] = useState("");
  const dropProps = (id: string | null) => ({
    onDragOver: (e: DragEvent) => {
      if ((onDropItems && canDrop?.(id)) || onMove) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      } else e.dataTransfer.dropEffect = "none";
    },
    onDrop: async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onDropItems) {
        onDropItems(e, id);
        return;
      }
      const source = e.dataTransfer.getData("text/plain");
      if (
        !onMove ||
        !source ||
        (id && descendantIds(folders, source).includes(id))
      )
        return;
      setError("");
      try {
        await onMove(source, id);
        invalidateLibrary(adapter);
      } catch (err) {
        setError(libraryError(err));
      }
    },
  });
  const render = (
    parent: string | null,
    depth = 0,
    ancestors: string[] = [],
  ): React.ReactNode =>
    folders
      .filter((f) => f.parentId === parent && !ancestors.includes(f.id))
      .map((f) => {
        const children = folders.some((c) => c.parentId === f.id);
        const counts = folderCounts(folders, f.id);
        const count = Object.entries(counts).reduce(
          (n, [s, v]) => n + (s === "archived" ? 0 : (v ?? 0)),
          0,
        );
        return (
          <div key={f.id}>
            <div
              className="ml-tree-row"
              style={{ paddingLeft: depth * 14 }}
              draggable={!!onMove || !!onDropItems}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", f.id);
                e.dataTransfer.setData(
                  libraryDragType,
                  JSON.stringify({
                    customerId,
                    entries: [{ id: f.id, kind: "folder" }],
                  }),
                );
                e.dataTransfer.effectAllowed = "move";
              }}
              {...dropProps(f.id)}
            >
              {children ? (
                <button
                  type="button"
                  aria-label={
                    f.name + (collapsed.includes(f.id) ? " 펼치기" : " 접기")
                  }
                  aria-expanded={!collapsed.includes(f.id)}
                  onClick={() =>
                    setCollapsed((v) =>
                      v.includes(f.id)
                        ? v.filter((x) => x !== f.id)
                        : [...v, f.id],
                    )
                  }
                >
                  <ChevronRight
                    size={13}
                    style={{
                      transform: collapsed.includes(f.id)
                        ? ""
                        : "rotate(90deg)",
                    }}
                  />
                </button>
              ) : (
                <span className="ml-tree-indent" />
              )}
              <button
                type="button"
                aria-pressed={value === f.id}
                onClick={() => onSelect(f.id)}
              >
                {f.targetCustomerId && !f.targetInherited ? (
                  <Building2 size={16} />
                ) : value === f.id ? (
                  <FolderOpen size={16} />
                ) : (
                  <Folder size={16} />
                )}
                <span>
                  {f.name}
                  {f.targetCustomerId && (
                    <small
                      className={
                        "ml-target-badge" +
                        (f.targetInherited ? " inherited" : "")
                      }
                    >
                      대상:{" "}
                      {adapter.customers.find(
                        (c) => c.id === f.targetCustomerId,
                      )?.name ?? f.targetCustomerId}
                      {f.targetInherited ? " · 상속" : ""}
                    </small>
                  )}
                </span>
                <small>{count}</small>
              </button>
            </div>
            {!collapsed.includes(f.id) &&
              render(f.id, depth + 1, [...ancestors, f.id])}
          </div>
        );
      });
  return (
    <nav className="ml-tree" aria-label="원고 폴더">
      {(resource.error || error) && (
        <p role="alert" className="ml-error">
          {resource.error || error}
          <Button variant="ghost" onClick={resource.retry}>
            다시 읽기
          </Button>
        </p>
      )}
      <button
        type="button"
        aria-pressed={value === "all"}
        onClick={() => onSelect("all")}
        {...dropProps(null)}
      >
        <HardDrive size={16} />
        {rootLabel ?? libraryRoot(adapter, customerId)}
      </button>
      <button
        type="button"
        title="어느 폴더에도 넣지 않은 원고만 모아 봅니다"
        aria-pressed={value === "none"}
        onClick={() => onSelect("none")}
        {...dropProps(null)}
      >
        <FolderOpen size={16} />
        <span>폴더 밖 원고</span>
        <small>
          {resource.data
            ? Object.entries(resource.data.unfiled).reduce(
                (n, [s, v]) => n + (s === "archived" ? 0 : (v ?? 0)),
                0,
              )
            : "—"}
        </small>
      </button>
      {render(null)}
    </nav>
  );
}
export async function droppedFiles(data: DataTransfer): Promise<File[]> {
  const entries = Array.from(data.items)
    .map((i) => i.webkitGetAsEntry?.())
    .filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return Array.from(data.files);
  const walk = async (entry: FileSystemEntry, prefix = ""): Promise<File[]> => {
    const path = prefix + entry.name;
    if (entry.isFile)
      return new Promise((resolve, reject) =>
        (entry as FileSystemFileEntry).file((file) => {
          Object.defineProperty(file, "webkitRelativePath", {
            value: path,
            configurable: true,
          });
          resolve([file]);
        }, reject),
      );
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const children: FileSystemEntry[] = [];
    while (true) {
      const part = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      if (!part.length) break;
      children.push(...part);
    }
    return (await Promise.all(children.map((e) => walk(e, path + "/")))).flat();
  };
  return (await Promise.all(entries.map((e) => walk(e)))).flat();
}
